import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL, ADMIN_API_BASE_URL, XMEDICAL_CONFIG } from '../../config';
import { SessionStorageService } from '../../storage';

/**
 * HTTP Interceptor that adds the WebSocket/Pusher socket ID header to API requests.
 *
 * This enables the server to correlate HTTP requests with WebSocket connections
 * for real-time communication features.
 *
 * Headers added:
 * - `x-socket-id`
 *
 * @example
 * { provide: HTTP_INTERCEPTORS, useClass: WebSocketInterceptor, multi: true }
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketInterceptor implements HttpInterceptor {
  private baseUrl = inject(API_BASE_URL);
  private adminUrl = inject(ADMIN_API_BASE_URL);
  private config = inject(XMEDICAL_CONFIG);
  private sessionStorage = inject(SessionStorageService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.indexOf(this.adminUrl) > -1 || request.url.indexOf(this.baseUrl) > -1) {
      return this.addSocketHeader(request, next);
    }
    return next.handle(request);
  }

  private addSocketHeader(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.config.storageKeys) {
      throw new Error('XMedicalConfig.storageKeys is required but not provided');
    }

    const socketKey = this.config.storageKeys.socketId;
    const socketId = this.sessionStorage.getItem(socketKey);

    if (socketId) {
      // Preserve existing headers
      const headerSettings: { [name: string]: string | string[]; } = {};
      for (const key of request.headers.keys()) {
        headerSettings[key] = request.headers.getAll(key);
      }

      headerSettings['x-socket-id'] = socketId;

      const newHeader = new HttpHeaders(headerSettings);
      const changedRequest = request.clone({ headers: newHeader });
      return next.handle(changedRequest);
    }

    return next.handle(request);
  }
}
